import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const WARNING_WINDOW_SEC = 5 * 60;      // show banner when <= 5 min remain
const CHECK_INTERVAL_MS = 30 * 1000;    // re-check every 30 s

export default function AdminSessionWarning() {
  const { user, refreshMe } = useAuth();
  const [remaining, setRemaining] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const tick = () => {
      const exp = user?.session_exp;
      if (!exp) { setRemaining(null); return; }
      const secs = Math.max(0, exp - Math.floor(Date.now() / 1000));
      setRemaining(secs);
      // Auto-refetch profile when exp is stale (after a manual refresh elsewhere)
      if (secs === 0) refreshMe?.();
    };
    tick();
    const id = setInterval(tick, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [user?.session_exp, refreshMe]);

  const staySignedIn = async () => {
    setRefreshing(true);
    try {
      await api.post("/auth/refresh");
      await refreshMe?.();
      setDismissed(false);
      toast.success("Session extended by 2 hours");
    } catch {
      toast.error("Couldn't refresh session — please sign in again");
    } finally {
      setRefreshing(false);
    }
  };

  if (remaining === null || remaining > WARNING_WINDOW_SEC || dismissed) return null;

  const m = Math.floor(remaining / 60), s = remaining % 60;
  const label = remaining <= 0 ? "expired" : `expires in ${m}m ${String(s).padStart(2,"0")}s`;

  return (
    <div data-testid="admin-session-warning"
         className="sticky top-0 z-40 bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-center gap-4 shadow-sm">
      <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
        <Clock className="w-4 h-4 text-amber-700"/>
      </div>
      <div className="flex-1 text-sm">
        <div className="font-outfit font-semibold text-amber-900" data-testid="admin-session-label">
          Your session {label}
        </div>
        <div className="text-xs text-amber-700">
          Click "Stay signed in" to extend by 2 more hours without re-entering your password.
        </div>
      </div>
      <button onClick={staySignedIn} disabled={refreshing}
              data-testid="admin-session-refresh"
              className="inline-flex items-center gap-2 bg-amber-900 text-amber-50 rounded-full px-4 py-2 text-sm font-medium hover:bg-amber-950 disabled:opacity-60">
        <RefreshCw className={`w-3.5 h-3.5 ${refreshing?"animate-spin":""}`}/>
        Stay signed in
      </button>
      <button onClick={()=>setDismissed(true)} data-testid="admin-session-dismiss"
              className="text-xs text-amber-800 hover:text-amber-950 px-2">Dismiss</button>
    </div>
  );
}
